import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IDropdownOption } from '@fluentui/react';
import {
  FluentProvider,
  MessageBar,
  MessageBarBody,
  webLightTheme,
} from '@fluentui/react-components';
import { Controller, UseFormMethods } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import LifecycleStatusDropdown from '../../common/formFields/LifecycleStatusDropdown';
import IDropdownReference from '../../../utils/types/IDropdownReference';
import { IFinanceIncompleteReviewApproval, IEntityTerminationActionItem } from '../types';
import {
  GET_LIFECYCLE_STATUSES_QUERY,
  GET_TERMINATION_LIFECYCLE_STATUSES_QUERY,
} from '../../../utils/statApi/EntityStatusApi';

const allowedLifecycleStatuses = [
  'Merged',
  'Dissolved',
  'Inactive',
  'Sold',
  'De-registered',
  'Amalgamated',
];

interface IFinanceIncompleteReviewApprovalFormProps {
  form: UseFormMethods<IFinanceIncompleteReviewApproval>;
  actionItems: IEntityTerminationActionItem[];
}

const FinanceIncompleteReviewApprovalForm = ({
  form,
  actionItems,
}: IFinanceIncompleteReviewApprovalFormProps): JSX.Element => {
  const { control, errors, setValue } = form;

  const onLifecycleStatusChange = (value: IDropdownOption): void => {
    setValue('lifecycleStatus', value, { shouldValidate: true });
  };

  const {
    loading: lifecycleLoading,
    data: lifecycleData,
    error: lifecycleError,
  } = useQuery(GET_LIFECYCLE_STATUSES_QUERY);

  const { loading: terminationLoading, error: terminationError } = useQuery(
    GET_TERMINATION_LIFECYCLE_STATUSES_QUERY,
  );

  const hasIncompleteActionItems = actionItems.some(
    (item) => !item.dateCompleted || (item.tasks ?? []).some((task) => !task.completedDate),
  );

  const lifecycleDropdownOptionsData =
    !lifecycleLoading && lifecycleData
      ? (lifecycleData.lifecycleStatuses as IDropdownReference[]).filter((status) =>
          allowedLifecycleStatuses.includes(status.fieldName),
        )
      : [];

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <LoadingErrorMessage
          loading={lifecycleLoading || terminationLoading}
          error={lifecycleError || terminationError}
        />
        {hasIncompleteActionItems && (
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              You cannot approve this Finance Incomplete review until all action items and tasks are
              complete.
            </MessageBarBody>
          </MessageBar>
        )}
        <Controller
          name="lifecycleStatus"
          label="Lifecycle Status"
          valueKey="lifecycleStatus"
          control={control}
          defaultValue={null}
          render={(props: { value: IDropdownReference }): JSX.Element => (
            <LifecycleStatusDropdown
              label="Lifecycle Status"
              valueKey="lifecycleStatus"
              required
              errors={errors}
              handleChange={onLifecycleStatusChange}
              value={props.value}
              dropdownOptionsData={lifecycleDropdownOptionsData}
            />
          )}
          rules={{
            validate: (value?: IDropdownReference): string | undefined =>
              value?.fieldName?.trim() ? undefined : 'Please select a Lifecycle Status.',
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default FinanceIncompleteReviewApprovalForm;
